import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  getOptInSettings,
  getPendingOptIns,
  resendOptIn,
  updateOptInSettings,
} from '../services/opt-in-settings.service';
import type {
  GetPendingOptInsQuery,
  OptInSettings,
  PendingOptInsResult,
  ResendOptInInput,
  ResendOptInResult,
  UpdateOptInSettingsInput,
} from '../types/opt-in-settings.types';

export const OPT_IN_SETTINGS_QUERY_KEY = ['settings', 'optin'] as const;
export const OPT_IN_PENDING_QUERY_KEY = ['settings', 'optin', 'pending'] as const;

export const useGetOptInSettings = () =>
  useGlobalQuery<OptInSettings>({
    queryKey: OPT_IN_SETTINGS_QUERY_KEY,
    queryFn: getOptInSettings,
    staleTime: 30 * 1000,
  });

export const useUpdateOptInSettings = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<OptInSettings, Error, UpdateOptInSettingsInput>({
    mutationFn: updateOptInSettings,
    onSuccess: (settings) => {
      queryClient.setQueryData(OPT_IN_SETTINGS_QUERY_KEY, settings);
      queryClient.invalidateQueries({ queryKey: OPT_IN_SETTINGS_QUERY_KEY });
    },
  });
};

export const useGetPendingOptIns = (query: GetPendingOptInsQuery = {}) => {
  const limit = query.limit ?? 50;
  const offset = query.offset ?? 0;

  return useGlobalQuery<PendingOptInsResult>({
    queryKey: [...OPT_IN_PENDING_QUERY_KEY, { limit, offset }],
    queryFn: () => getPendingOptIns({ limit, offset }),
    staleTime: 30 * 1000,
  });
};

export const useResendOptIn = () => {
  return useGlobalMutation<ResendOptInResult, Error, ResendOptInInput>({
    mutationFn: resendOptIn,
    suppressToast: true,
  });
};
